import React from 'react';
import type { ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { ErrorBoundary } from 'react-error-boundary';
import { useGate } from 'statsig-react';
import type { ExecutionResultWithData } from '@soundxyz/graphql-react-query';
import { ERROR_TYPE, GLOBAL_ERROR_ACTIONS, PILLARS } from '@soundxyz/utils/constants/logging';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { styled } from '@components-shared/src/stitches';
import { LEFT_NAV_WIDTH_COLLAPSED, LEFT_NAV_WIDTH_FULL } from '@components/LeftNav/LeftNav';
import { FullScreenLoading } from '@components/basics/FullScreenLoading';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { useConnectedAuth } from '@context/auth/connected';
import { MediaQuery } from '@context/mediaQuery';
import type { LearnMoreFooterQuery } from '@graphql/reactQuery';
import { useIsSsr } from '@hooks/useIsSsr';
import { useKeyboardShortcuts } from '@hooks/useKeyboardShortcuts';
import { useLogError } from '@hooks/useLogErrorNew';
import { FEATURE_GATES, HELP_PATHS } from '@utils/constants';

const DynamicFooter = dynamic(() => import('./Footer').then(mod => mod.Footer));

const DynamicLeftNav = dynamic(() =>
  import('@components/LeftNav/LeftNav').then(mod => mod.LeftNav),
);

const DynamicBottomMobileNavV2 = dynamic(() =>
  import('@components/MobileNav/MobileNavV2').then(mod => mod.MobileNavV2),
);

const DynamicHeader = dynamic(() => import('@components/Header').then(mod => mod.Header));

const DynamicAudioPlayer = dynamic(
  () => import('@components/AudioPlayer/AudioPlayer').then(mod => mod.AudioPlayer),
  {
    ssr: false,
  },
);

const DynamicFeedBanner = dynamic(
  () => import('@components/Header/Banner/NewFeedFeature').then(mod => mod.NewFeedFeatureBanner),
  {
    ssr: false,
  },
);

export interface SharedProps {
  learnMore: ExecutionResultWithData<LearnMoreFooterQuery> | undefined;
}

interface LayoutProps extends SharedProps {
  children: ReactNode;
}

const HIDDEN_FOOTER_ROUTES = [
  { path: '/playlist/[id]' },
  { path: '/playlist/new' },
  { path: '/playlist/[id]/edit' },
  { path: '/user/[publicAddress]/featured/edit' },
  { path: '/dashboard' },
  { path: '/dashboard/earnings' },
  { path: '/[soundHandle]/[releaseSlug]' },
  { path: '/signin' },
  { path: '/search' },
  { path: '/explore' },
];

const HIDDEN_MOBILE_BOTTOM_NAV_ROUTES = [
  { path: '/playlist/new' },
  { path: '/playlist/[id]/edit' },
  { path: '/user/[publicAddress]/featured/edit' },
  { path: '/dashboard' },
  { path: '/dashboard/earnings' },
];

export const HIDDEN_FEATURE_BANNER_ROUTES = [
  { path: '/feed' },
  { path: '/dashboard' },
  { path: '/artist-application' },
];

const HIDDEN_MOBILE_TOP_NAV_ROUTES: { path: string }[] = [{ path: '/[soundHandle]/post/[uuid]' }];

export const Layout = ({ children, ...sharedProps }: LayoutProps) => {
  const { isLoggingOut: loggingOut } = useConnectedAuth();
  const isSsr = useIsSsr();
  const { pathname } = useRouter();
  const { tablet } = MediaQuery.useContainer();
  useKeyboardShortcuts();
  const logError = useLogError();
  const { value: commentSectionEnabled } = useGate(FEATURE_GATES.SONG_PAGE_COMMENT_SECTION);

  const overflowXInitial =
    pathname.includes('/[soundHandle]/[releaseSlug]') ||
    pathname.includes('/dashboard/earnings') ||
    pathname.includes('/playlist/[id]') ||
    pathname.includes('/notifications') ||
    pathname.includes('/[soundHandle]/post/[uuid]') ||
    pathname.includes('/feed');

  const showNewFeatureBanner = !HIDDEN_FEATURE_BANNER_ROUTES.some(route =>
    pathname.includes(route.path),
  );

  const mobileOverflowXInitial =
    commentSectionEnabled && pathname.includes('/[soundHandle]/[releaseSlug]');

  const isReleasePreview =
    pathname.includes('/step/[stepId]/preview') || pathname.includes('/step/[stepId]/edit/preview');

  const hideFooter = HIDDEN_FOOTER_ROUTES.some(route => pathname.includes(route.path));
  const hideBottomMobileNav = HIDDEN_MOBILE_BOTTOM_NAV_ROUTES.some(route =>
    pathname.includes(route.path),
  );

  const hideTopMobileNav = HIDDEN_MOBILE_TOP_NAV_ROUTES.some(route =>
    pathname.includes(route.path),
  );

  const handleError = useStableCallback((error, { componentStack }) => {
    logError({
      action: GLOBAL_ERROR_ACTIONS.GLOBAL_PAGE_CRASH,
      error,
      errorType: ERROR_TYPE.UNKNOWN,
      pillar: PILLARS.GLOBAL,
      isArtistOnlyPage: false,
      message: 'An error crashed the page',
      level: 'fatal',
      indexedTags: {
        pathname,
      },
      unindexedExtra: {
        componentStack,
      },
    });
  });

  return (
    <>
      {(tablet || isSsr) && <DynamicLeftNav />}
      {!hideBottomMobileNav && <DynamicBottomMobileNavV2 />}
      <Box
        id="layout"
        withLeftNav={!isReleasePreview}
        transition={{ duration: 0.5, ease: 'easeInOut' }}
        css={{
          overflowX: mobileOverflowXInitial ? 'initial' : 'hidden',
          '@tablet': {
            overflowX: overflowXInitial ? 'initial' : 'hidden',
          },
        }}
      >
        <ErrorBoundary fallback={<FallbackUI />} onError={handleError}>
          {showNewFeatureBanner && (
            <AnimatePresence>
              <div className="hidden md:block">
                <DynamicFeedBanner />
              </div>
            </AnimatePresence>
          )}
          <DynamicHeader
            hideMobileNav={hideTopMobileNav}
            showNewFeatureBanner={showNewFeatureBanner}
          />

          {loggingOut ? (
            <FullScreenLoading />
          ) : (
            <>
              <Box as="main">{children}</Box>
              {!hideFooter && <DynamicFooter {...sharedProps} />}
            </>
          )}
        </ErrorBoundary>
      </Box>
      <DynamicAudioPlayer />
    </>
  );
};

// WARNING!: Be very careful if applying styles to this.
// ex: `overflow: hidden` will break the layout.
const Box = styled(motion.div, {
  variants: {
    withLeftNav: {
      true: {
        '@tablet': {
          marginLeft: LEFT_NAV_WIDTH_COLLAPSED,
        },
        '@laptop': {
          marginLeft: LEFT_NAV_WIDTH_FULL,
        },
      },
    },
  },
});

const FallbackUI = () => (
  <div className="flex min-h-screen w-full items-center justify-center bg-base200 px-4 pb-12 md:px-6 md:pb-16 2xl:max-w-content">
    <div className="flex flex-col items-center justify-center gap-3 text-center md:gap-4">
      <span className="text-title-xxl">🫤</span>
      <h1 className="text-title-l font-semibold md:text-title-xl ">Something went wrong</h1>
      <div className="flex max-w-lg flex-col gap-y-4 font-base text-base-m text-base400 md:gap-y-5 md:text-base-l">
        <p>
          Oops! It seems we&apos;ve hit a snag. <br />
          Our team is already taking an action to untangle this.
        </p>
        <p>
          While we&apos;re on it, why not try giving your browser a quick refresh? If the hiccup
          persists, we&apos;re just a&nbsp;
          <AccessibleLink
            absoluteUri={HELP_PATHS.RequestHelp}
            className="underline"
            type="external"
          >
            click away for support.
          </AccessibleLink>
        </p>
      </div>
    </div>
  </div>
);
